import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Phone, Plus, X, Search, Check, PhoneCall, Zap, MoreHorizontal,
  RefreshCw, Edit3, Copy, Trash2, ChevronDown, GripVertical, Bot,
} from 'lucide-react';
import {
  DndContext, PointerSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay,
} from '@dnd-kit/core';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut, apiDel } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import LeadDrawer from '../components/LeadDrawer.jsx';
import AIDialSelectedModal from '../components/AIDialSelectedModal.jsx';
import { fmtFromNow, fmtPhone } from '../lib/format.js';
import { useAuth } from '../store/auth.js';
import { usePowerDial } from '../state/powerDial.js';
import { usePipelineSelection } from '../state/pipelineSelection.js';

const STAGE_PALETTE = ['#8b5cf6','#a855f7','#6366f1','#22d3ee','#06b6d4','#10b981','#84cc16','#22c55e','#f59e0b','#ef4444','#ec4899','#f97316'];

export default function SalesPipeline() {
  const qc = useQueryClient();
  const isAdmin = useAuth((s) => ['admin', 'superadmin'].includes(s.agent?.role));
  const [activePipelineId, setActivePipelineId] = useState(null);
  const [showNewPipeline, setShowNewPipeline] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [autoDial, setAutoDial] = useState(false);
  const [adding, setAdding] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(null);
  // Stage being targeted by the "AI Dial This Stage" column-menu action.
  const [aiDialStage, setAIDialStage] = useState(null);

  // Power Dial cross-column selection state. Cleared on unmount so /pipeline
  // visits start clean.
  const openPowerDialModal = usePowerDial((s) => s.openModal);
  const pipelineSelection = usePipelineSelection();
  useEffect(() => () => pipelineSelection.clear(), []); // eslint-disable-line

  async function deleteLead(lead) {
    if (!confirm(`Delete ${lead.firstName || ''} ${lead.lastName || ''}? This cannot be undone.`)) return;
    try {
      await apiDel(`/leads/${lead._id}`);
      toast.success('Lead deleted');
      qc.invalidateQueries({ queryKey: ['pipeline-board'] });
      qc.invalidateQueries({ queryKey: ['leads'] });
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Delete failed');
    }
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // Pipelines list
  const { data: pipesData } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => apiGet('/pipelines'),
  });
  // Single self-only "My Pipeline" list — server merges shared default
  // sub-pipelines (Mortgage Protection, IUL, Final Expense, Term, Annuity,
  // …) with the caller's personal pipelines into `mine`. The legacy
  // `company` array is unused on this page; cross-agent viewing lives at
  // Workspace → Leads.
  const list = pipesData?.pipelines?.mine || [];

  // Default selection
  useEffect(() => {
    if (!list.length) { setActivePipelineId(null); return; }
    if (!list.find(p => p._id === activePipelineId)) {
      setActivePipelineId(list[0]._id);
    }
  }, [list]); // eslint-disable-line

  const { data: boardData, refetch: refetchBoard } = useQuery({
    queryKey: ['pipeline-board', activePipelineId],
    queryFn: () => activePipelineId ? apiGet(`/pipelines/${activePipelineId}/leads`) : null,
    enabled: !!activePipelineId,
  });
  const pipeline = boardData?.pipeline;
  const byStage = boardData?.byStage || {};
  // canEditStages: admin/superadmin can always edit; agents can only edit their own personal pipelines.
  const canEditStages = isAdmin || (pipeline?.scope === 'personal' && String(pipeline?.owner) === String(useAuth.getState().agent?._id));
  const stages = useMemo(
    () => (pipeline?.stages || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [pipeline]
  );

  // Default caller ID falls through to backend's smart-pick (NumberHealth) when null.
  const defaultCallerId = '+14068604882';

  function refresh() {
    qc.invalidateQueries({ queryKey: ['pipeline-board', activePipelineId] });
    qc.invalidateQueries({ queryKey: ['pipelines'] });
  }

  async function dialLead(lead) {
    try {
      const r = await apiPost('/dialer/dial', { to: lead.phone, from: defaultCallerId, leadId: lead._id });
      if (r?.ok) {
        toast.success(`Dialing ${lead.firstName || ''}…`);
        refresh();
      } else { toast.error('Dial failed'); }
    } catch { toast.error('Dial failed'); }
  }

  // Power Dial — open modal preloaded with this stage's leads. If the agent
  // has cross-column cards selected via checkbox, the selection wins.
  function powerDial(stageLeads, stageName) {
    const selectedIds = pipelineSelection.ids();
    let leadsForModal;
    if (selectedIds.length > 0) {
      leadsForModal = pipelineSelection.values();
    } else if (stageLeads.length === 0) {
      toast('No leads in this stage');
      return;
    } else {
      leadsForModal = stageLeads;
    }
    const ids = leadsForModal.map((l) => l._id);
    const index = {};
    for (const l of leadsForModal) {
      index[l._id] = { firstName: l.firstName, lastName: l.lastName, phone: l.phone };
    }
    openPowerDialModal(ids, index);
    if (selectedIds.length === 0) {
      // pre-fill selection so the user can see / refine it
      pipelineSelection.setMany(leadsForModal);
    }
  }

  async function moveLead(leadId, toStageId) {
    try {
      await apiPost(`/pipelines/${pipeline._id}/move-lead`, { leadId, toStageId });
      refresh();
      if (autoDial) {
        const lead = Object.values(byStage).flat().find(l => l._id === leadId);
        if (lead) {
          try { await apiPost('/dialer/dial', { to: lead.phone, from: defaultCallerId, leadId: lead._id, mode: 'auto' }); } catch {}
        }
      }
    } catch { toast.error('Move failed'); }
  }

  async function addStage() {
    if (!canEditStages) { toast.error('Admin only: cannot add stages to a shared pipeline'); return; }
    const name = prompt('New stage name?');
    if (!name?.trim()) return;
    const color = STAGE_PALETTE[stages.length % STAGE_PALETTE.length];
    try {
      await apiPost(`/pipelines/${pipeline._id}/stages`, { name: name.trim(), color });
      toast.success('Stage added');
      refresh();
    } catch { toast.error('Failed to add stage'); }
  }

  async function deleteStage(stage) {
    if (!canEditStages) { toast.error('Admin only: cannot delete stages from a shared pipeline'); return; }
    if (stages.length <= 1) { toast.error('Cannot delete last stage'); return; }
    if (!confirm(`Delete stage "${stage.name}"? Leads will move to the first remaining stage.`)) return;
    try {
      await apiDel(`/pipelines/${pipeline._id}/stages/${stage._id}`);
      toast.success('Stage deleted');
      refresh();
    } catch { toast.error('Delete failed'); }
  }

  async function cloneStage(stage) {
    if (!canEditStages) { toast.error('Admin only: cannot clone stages on a shared pipeline'); return; }
    try {
      await apiPost(`/pipelines/${pipeline._id}/stages`, {
        name: `${stage.name} (Copy)`,
        color: stage.color,
        autoDial: stage.autoDial,
        callScript: stage.callScript,
        timezoneFilter: stage.timezoneFilter,
      });
      toast.success('Stage cloned');
      refresh();
    } catch { toast.error('Clone failed'); }
  }

  async function deletePipeline() {
    if (!pipeline) return;
    if (!confirm(`Delete pipeline "${pipeline.name}"? Leads remain but are unassigned.`)) return;
    try {
      await apiDel(`/pipelines/${pipeline._id}`);
      toast.success('Pipeline deleted');
      setActivePipelineId(null);
      refresh();
    } catch { toast.error('Delete failed'); }
  }

  async function clonePipeline() {
    if (!pipeline) return;
    try {
      const r = await apiPost(`/pipelines/${pipeline._id}/clone`, {});
      if (r?.pipeline?._id) {
        setActivePipelineId(r.pipeline._id);
        toast.success('Pipeline cloned to My Pipelines');
        refresh();
      }
    } catch { toast.error('Clone failed'); }
  }

  return (
    <div className="space-y-3 h-full flex flex-col">
      <TopBar title="Sales Pipeline" subtitle="Workflow" />
      <PipelineSelectionBar onPowerDial={() => powerDial([], '')} />

      {/* "My Pipeline" header + sub-pipeline picker. Always self-only — no
          tab/segmented control, no admin override, no "view as agent". For
          cross-agent lead viewing, use Workspace → Leads. */}
      <div data-testid="pipeline-toolbar" className="panel p-3 flex flex-wrap items-center gap-3">
        <div className="px-3 py-1.5 text-sm font-semibold text-white">My Pipeline</div>

        <PipelinePicker list={list} activeId={activePipelineId} onPick={setActivePipelineId}/>

        <button onClick={() => setShowNewPipeline(true)} className="btn-primary text-sm flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> New Pipeline
        </button>

        {pipeline && (
          <PipelineMenu
            onRefresh={refresh}
            onClone={clonePipeline}
            onDelete={deletePipeline}
          />
        )}

        <div className="flex-1"/>

        <button
          onClick={async () => {
            try {
              const r = await apiPost('/pipelines/backfill', {});
              toast.success(`Backfilled ${r.modified || 0} leads → ${r.pipeline?.name}`);
              qc.invalidateQueries({ queryKey: ['pipeline-board'] });
              qc.invalidateQueries({ queryKey: ['pipelines'] });
            } catch (e) {
              toast.error(e?.message || 'Backfill failed');
            }
          }}
          className={`text-xs px-3 py-1.5 rounded-md border border-white/15 hover:border-civix-purple/60 hover:bg-civix-purple/10 transition ${isAdmin ? '' : 'hidden'}`}
          title="Assign legacy leads (no pipeline) to the Default company pipeline"
        >
          Backfill legacy leads
        </button>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={autoDial} onChange={(e) => setAutoDial(e.target.checked)} className="accent-civix-purple"/>
          <Zap className={`w-4 h-4 ${autoDial ? 'text-civix-cyan' : 'text-white/40'}`}/>
          Auto-dial moves
        </label>
      </div>

      {/* Kanban board: horizontal scroll */}
      {pipeline ? (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setDraggedLead(e.active.data.current?.lead || null)}
          onDragEnd={(e) => {
            const lead = e.active.data.current?.lead;
            const toStageId = e.over?.id;
            setDraggedLead(null);
            if (lead && toStageId && String(lead.stage) !== String(toStageId)) {
              moveLead(lead._id, toStageId);
            }
          }}
        >
          <div className="flex-1 overflow-x-auto overflow-y-hidden pb-2">
            <div className="flex gap-3 min-h-full" style={{ minWidth: 'max-content' }}>
              {stages.map((stage) => (
                <StageColumn
                  key={stage._id}
                  stage={stage}
                  leads={byStage[String(stage._id)] || []}
                  canEditStages={canEditStages}
                  onAdd={() => setAdding(stage)}
                  onPowerDial={() => powerDial(byStage[String(stage._id)] || [], stage.name)}
                  onAIDial={() => setAIDialStage(stage)}
                  onDial={dialLead}
                  onOpenLead={(l) => setOpenLeadId(l._id)}
                  onDeleteLead={deleteLead}
                  onRefresh={refresh}
                  onEdit={() => setEditingStage(stage)}
                  onClone={() => cloneStage(stage)}
                  onDelete={() => deleteStage(stage)}
                />
              ))}
              {canEditStages && (
              <button
                onClick={addStage}
                className="self-start mt-2 min-w-[280px] h-32 rounded-lg border-2 border-dashed border-civix-border/50 hover:border-civix-purple hover:bg-civix-purple/5 transition flex flex-col items-center justify-center gap-2 text-white/55 hover:text-civix-purple"
              >
                <Plus className="w-6 h-6"/>
                <span className="font-display font-bold text-sm uppercase tracking-wider">New Stage</span>
              </button>
              )}
            </div>
          </div>

          <DragOverlay>
            {draggedLead && <LeadCard lead={draggedLead} dimmed/>}
          </DragOverlay>
        </DndContext>
      ) : (
        <div className="panel p-12 text-center text-white/50">
          {list.length === 0 ? 'No pipelines yet. Click “New Pipeline” to create one.' : 'Loading…'}
        </div>
      )}

      {showNewPipeline && (
        <NewPipelineDrawer
          isAdmin={isAdmin}
          onClose={() => setShowNewPipeline(false)}
          onCreated={(p) => {
            setShowNewPipeline(false);
            setActivePipelineId(p._id);
            refresh();
          }}
        />
      )}

      {editingStage && pipeline && (
        <EditStageDrawer
          pipelineId={pipeline._id}
          stage={editingStage}
          onClose={() => setEditingStage(null)}
          onSaved={() => { setEditingStage(null); refresh(); }}
        />
      )}

      {adding && pipeline && (
        <AddLeadsDrawer
          pipelineId={pipeline._id}
          stage={adding}
          onClose={() => setAdding(null)}
          onSubmit={async (ids) => {
            for (const id of ids) {
              try { await apiPost(`/pipelines/${pipeline._id}/move-lead`, { leadId: id, toStageId: adding._id }); } catch {}
            }
            toast.success(`Added ${ids.length} → ${adding.name}`);
            setAdding(null);
            refresh();
          }}
        />
      )}

      {openLeadId && (
        <LeadDrawer
          leadId={openLeadId}
          onClose={() => setOpenLeadId(null)}
          onChange={() => { refresh(); qc.invalidateQueries({ queryKey: ['leads'] }); }}
        />
      )}

      <AIDialSelectedModal
        open={!!aiDialStage}
        onClose={() => setAIDialStage(null)}
        leadIds={(aiDialStage ? (byStage[String(aiDialStage._id)] || []) : []).map((l) => l._id)}
        contextLabel={aiDialStage ? `Stage: ${aiDialStage.name}` : ''}
        onLaunched={() => setAIDialStage(null)}
      />
    </div>
  );
}

/* ---------------------------------------------------------------- */
function PipelinePicker({ list, activeId, onPick }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 200 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 280) });
    }
  }, [open]);

  const active = list.find(p => p._id === activeId);
  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(o => !o)}
        className="px-3 py-1.5 rounded-lg bg-civix-bg/40 border border-civix-border/40 text-sm flex items-center gap-2 hover:border-civix-purple/50 min-w-[200px]">
        <span className="font-medium truncate flex-1 text-left">{active?.name || 'Select pipeline'}</span>
        <ChevronDown className="w-4 h-4 text-white/40"/>
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            maxHeight: 384,
            overflowY: 'auto',
            background: '#0f0f24',
            border: '1px solid rgba(139,92,246,0.5)',
            borderRadius: 8,
            boxShadow: '0 24px 60px rgba(0,0,0,0.9), 0 0 0 1px rgba(139,92,246,0.25)',
            zIndex: 99999,
          }}
        >
          {list.length === 0 && (
            <div style={{ padding: 16, fontSize: 13, color: '#fff', textAlign: 'center' }}>
              No pipelines yet.<br/>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                Click <span style={{ color: '#22d3ee', fontWeight: 600 }}>+ New Pipeline</span> to create one.
              </span>
            </div>
          )}
          {list.map(p => (
            <button key={p._id} onClick={() => { onPick(p._id); setOpen(false); }}
              style={{
                width: '100%', textAlign: 'left', padding: '8px 12px',
                background: p._id === activeId ? 'rgba(139,92,246,0.18)' : 'transparent',
                color: '#fff', border: 0, cursor: 'pointer',
              }}
              onMouseEnter={e => { if (p._id !== activeId) e.currentTarget.style.background = 'rgba(139,92,246,0.10)'; }}
              onMouseLeave={e => { if (p._id !== activeId) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: 13, fontWeight: 500 }}>
                {p.name}
                {p.isDefault && <span style={{ fontSize: 10, color: '#22d3ee', marginLeft: 6 }}>DEFAULT</span>}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{p.stages?.length || 0} stages</div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

/* ---------------------------------------------------------------- */
function PipelineMenu({ onRefresh, onClone, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    function h(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - 176 });
    }
  }, [open]);

  return (
    <>
      <button ref={btnRef} onClick={() => setOpen(o=>!o)} className="p-1.5 rounded-lg border border-civix-border/40 hover:border-civix-purple/50">
        <MoreHorizontal className="w-4 h-4 text-white/70"/>
      </button>
      {open && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: 176,
            background: '#0f0f24', border: '1px solid rgba(139,92,246,0.5)',
            borderRadius: 8, padding: '4px 0',
            boxShadow: '0 24px 60px rgba(0,0,0,0.9), 0 0 0 1px rgba(139,92,246,0.25)',
            zIndex: 99999,
          }}
        >
          <MenuItem icon={RefreshCw} label="Refresh" onClick={() => { onRefresh(); setOpen(false); }}/>
          <MenuItem icon={Copy} label="Clone Pipeline" onClick={() => { onClone(); setOpen(false); }}/>
          <MenuItem icon={Trash2} label="Delete Pipeline" onClick={() => { onDelete(); setOpen(false); }} danger/>
        </div>,
        document.body
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', padding: '6px 12px', fontSize: 13,
        display: 'flex', alignItems: 'center', gap: 8, background: 'transparent',
        color: danger ? '#f87171' : 'rgba(255,255,255,0.9)', border: 0, cursor: 'pointer',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.12)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <Icon style={{ width: 14, height: 14 }}/> {label}
    </button>
  );
}

/* ---------------------------------------------------------------- */
function StageColumn({ stage, leads, canEditStages, onAdd, onPowerDial, onAIDial, onDial, onOpenLead, onDeleteLead, onRefresh, onEdit, onClone, onDelete }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage._id });
  const [menuOpen, setMenuOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    function h(e) {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setMenuOpen(false);
    }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (menuOpen && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.right - 176 });
    }
  }, [menuOpen]);

  const headerStyle = {
    background: `linear-gradient(135deg, ${stage.color || '#8b5cf6'}, ${stage.color || '#22d3ee'}cc)`,
  };

  return (
    <div className="flex flex-col w-[300px] flex-none">
      {/* Sticky header */}
      <div className="sticky top-0 rounded-t-lg overflow-hidden" style={{ zIndex: 1 }}>
        <div className="px-3 py-2 flex items-center justify-between" style={headerStyle}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="font-display font-bold text-sm uppercase tracking-wider truncate">{stage.name}</div>
            <div className="text-xs bg-black/25 px-1.5 py-0.5 rounded-sm">{leads.length}</div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onAdd} title="Add leads" className="text-white/85 hover:text-white p-1">
              <Plus className="w-4 h-4"/>
            </button>
            <button ref={btnRef} onClick={() => setMenuOpen(o=>!o)} className="text-white/85 hover:text-white p-1">
              <MoreHorizontal className="w-4 h-4"/>
            </button>
          </div>
        </div>
      </div>
      {menuOpen && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: 176,
            background: '#0f0f24', border: '1px solid rgba(139,92,246,0.5)',
            borderRadius: 8, padding: '4px 0',
            boxShadow: '0 24px 60px rgba(0,0,0,0.9), 0 0 0 1px rgba(139,92,246,0.25)',
            zIndex: 99999,
          }}
        >
          <MenuItem icon={RefreshCw} label="Refresh" onClick={() => { onRefresh(); setMenuOpen(false); }}/>
          <MenuItem icon={Bot} label="AI Dial This Stage" onClick={() => { onAIDial && onAIDial(); setMenuOpen(false); }}/>
          {canEditStages && <MenuItem icon={Edit3} label="Edit Stage" onClick={() => { onEdit(); setMenuOpen(false); }}/>}
          {canEditStages && <MenuItem icon={Copy} label="Clone Stage" onClick={() => { onClone(); setMenuOpen(false); }}/>}
          {canEditStages && <MenuItem icon={Trash2} label="Delete Stage" onClick={() => { onDelete(); setMenuOpen(false); }} danger/>}
        </div>,
        document.body
      )}

      {/* Body — vertical scroll */}
      <div
        ref={setNodeRef}
        className={`flex-1 bg-civix-panel border border-civix-border/60 border-t-0 rounded-b-lg p-2 overflow-y-auto transition ${
          isOver ? 'bg-civix-purple/10 border-civix-purple' : ''
        }`}
        style={{ maxHeight: 'calc(100vh - 240px)', minHeight: '460px' }}
      >
        {stage.timezoneFilter && (
          <div className="text-[10px] uppercase tracking-wider text-civix-cyan/80 px-1 pb-1">
            TZ: {stage.timezoneFilter}
          </div>
        )}
        <button onClick={onPowerDial}
          className="btn-primary w-full text-xs mb-2 flex items-center justify-center gap-1.5">
          <PhoneCall className="w-3 h-3"/> Power Dial Queue
        </button>

        <div className="space-y-2">
          {leads.map(l => (
            <DraggableLeadCard key={l._id} lead={l} onDial={onDial} onOpen={onOpenLead} onDelete={onDeleteLead}/>
          ))}
          {leads.length === 0 && (
            <div className="text-xs text-white/35 px-2 py-6 text-center">Drop leads here</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
function PipelineSelectionBar({ onPowerDial }) {
  const count = usePipelineSelection((s) => s.selected.size);
  const clear = usePipelineSelection((s) => s.clear);
  if (count === 0) return null;
  return (
    <div className="panel p-2 flex items-center justify-between gap-3 border-civix-purple/40">
      <div className="flex items-center gap-2 text-sm">
        <span className="w-6 h-6 rounded-sm bg-civix-purple flex items-center justify-center">
          <Check className="w-4 h-4 text-white" />
        </span>
        <span className="font-semibold">{count} selected across columns</span>
        <button onClick={clear} className="text-xs text-white/55 hover:text-white underline ml-2">
          Clear
        </button>
      </div>
      <button
        onClick={onPowerDial}
        className="px-4 py-1.5 rounded-lg text-xs font-bold text-white
                   bg-linear-to-r from-[#7C3AED] to-[#E040FB]
                   hover:shadow-lg hover:shadow-[#7C3AED]/40 transition flex items-center gap-2"
      >
        <Zap className="w-4 h-4" /> Power Dial ({count})
      </button>
    </div>
  );
}

function DraggableLeadCard({ lead, onDial, onOpen, onDelete }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: lead._id,
    data: { lead },
  });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      className={`${isDragging ? 'opacity-30' : ''}`}
    >
      <LeadCard lead={lead} onDial={onDial} onOpen={onOpen} onDelete={onDelete} dragHandle={listeners}/>
    </div>
  );
}

function LeadCard({ lead, onDial, onOpen, onDelete, dragHandle, dimmed }) {
  const open = (e) => { e.stopPropagation(); onOpen?.(lead); };
  // Cross-column Power Dial selection. Reads via a per-store subscription so
  // the card re-renders when its own selection state flips.
  const isSelected = usePipelineSelection((s) => s.selected.has(lead?._id));
  const anySelected = usePipelineSelection((s) => s.selected.size > 0);
  const toggleSelection = usePipelineSelection((s) => s.toggle);
  return (
    <div className={`group relative bg-civix-panelLight border border-civix-border/40 rounded-md p-2 text-xs hover:border-civix-purple/50 transition ${dimmed ? 'shadow-2xl ring-2 ring-civix-purple' : ''} ${isSelected ? 'ring-2 ring-civix-purple/70 border-civix-purple/70' : ''}`}>
      {!dimmed && (
        <label
          className={`absolute top-1 left-1 z-10 w-5 h-5 rounded-sm grid place-items-center cursor-pointer transition ${isSelected || anySelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          onClick={(e) => e.stopPropagation()}
          title="Select for Power Dial"
        >
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={(e) => { e.stopPropagation(); toggleSelection(lead); }}
            className="w-3.5 h-3.5 accent-civix-purple cursor-pointer"
          />
        </label>
      )}
      {onDelete && !dimmed && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(lead); }}
          title="Delete lead"
          className="absolute top-1 right-1 w-5 h-5 rounded-sm grid place-items-center text-white/30 hover:text-red-400 hover:bg-red-500/15 opacity-0 group-hover:opacity-100 transition"
        >
          <X className="w-3 h-3"/>
        </button>
      )}
      <div className="flex items-start gap-1">
        <div {...(dragHandle || {})} className={`cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 -ml-1 pt-0.5 ${anySelected ? 'pl-4' : ''}`}>
          <GripVertical className="w-3 h-3"/>
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={open}
            className="font-medium truncate text-left hover:text-civix-cyan transition block w-full"
            title="Open lead"
          >
            {lead.firstName} {lead.lastName}
          </button>
          <button
            onClick={open}
            className="font-mono text-civix-purpleSoft text-[11px] hover:text-civix-cyan transition"
            title="Open lead"
          >
            {fmtPhone(lead.phone)}
          </button>
          <div className="text-white/50 mt-1 flex items-center gap-1.5 flex-wrap">
            <span>{lead.state || '—'}</span>
            <span className="text-white/25">·</span>
            <span>{lead.tier || 'Tier 2'}</span>
            {lead.priority === 'HOT' && <span className="text-[9px] bg-red-500/20 text-red-300 px-1 rounded-sm">HOT</span>}
          </div>
          {lead.quickNote && <div className="text-[10px] text-white/55 mt-1 italic truncate">"{lead.quickNote}"</div>}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 pl-3">
        <span className="text-white/40 text-[10px]">{fmtFromNow(lead.lastContactedAt || lead.updatedAt)}</span>
        {onDial && (
          <button
            onClick={(e) => { e.stopPropagation(); onDial(lead); }}
            title={`Dial ${fmtPhone(lead.phone)}`}
            className="w-7 h-7 rounded-full grid place-items-center bg-civix-purple/30 hover:bg-civix-purple/60 transition"
          >
            <Phone className="w-3 h-3"/>
          </button>
        )}
      </div>
      {lead.callCount > 0 && (
        <div className="text-[10px] text-white/40 mt-1 pl-3">{lead.callCount} call{lead.callCount === 1 ? '' : 's'}</div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- */
function NewPipelineDrawer({ isAdmin, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState('personal');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const r = await apiPost('/pipelines', { name: name.trim(), scope });
      if (r?.pipeline) onCreated(r.pipeline);
      else toast.error('Failed');
    } catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="panel w-full max-w-md p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">New <span className="gradient-text">Pipeline</span></h3>
          <button onClick={onClose}><X className="w-5 h-5 text-white/55"/></button>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/55">Name</label>
          <input className="input w-full mt-1" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. FEX Live Transfers"/>
        </div>
        {isAdmin && (
          <div>
            <label className="text-xs uppercase tracking-wider text-white/55">Visibility</label>
            <div className="flex gap-2 mt-1">
              <button onClick={()=>setScope('personal')}
                className={`flex-1 py-2 rounded-sm border text-sm ${scope==='personal' ? 'border-civix-purple bg-civix-purple/15' : 'border-civix-border/40'}`}>
                Just for me
              </button>
              <button onClick={()=>setScope('company')}
                className={`flex-1 py-2 rounded-sm border text-sm ${scope==='company' ? 'border-civix-purple bg-civix-purple/15' : 'border-civix-border/40'}`}>
                Shared sub-pipeline
              </button>
            </div>
            <div className="text-[11px] text-white/40 mt-1">
              Shared sub-pipelines appear in every teammate&apos;s My Pipeline dropdown. Each teammate still sees only their own assigned leads.
            </div>
          </div>
        )}
        <div className="text-[11px] text-white/45">9 default stages will be seeded. You can rename, reorder, add or remove afterwards.</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary">{busy ? 'Creating…' : 'Create Pipeline'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
function EditStageDrawer({ pipelineId, stage, onClose, onSaved }) {
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState(stage.color || '#8b5cf6');
  const [autoDial, setAutoDial] = useState(!!stage.autoDial);
  const [callScript, setCallScript] = useState(stage.callScript || '');
  const [timezoneFilter, setTimezoneFilter] = useState(stage.timezoneFilter || '');
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await apiPut(`/pipelines/${pipelineId}/stages/${stage._id}`, {
        name: name.trim(), color, autoDial, callScript, timezoneFilter,
      });
      toast.success('Stage saved');
      onSaved();
    } catch { toast.error('Save failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="panel w-full max-w-md p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Edit Stage</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-white/55"/></button>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/55">Name</label>
          <input className="input w-full mt-1" value={name} onChange={e=>setName(e.target.value)}/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/55 block mb-1">Color</label>
          <div className="flex flex-wrap gap-1.5">
            {STAGE_PALETTE.map(c => (
              <button key={c} onClick={()=>setColor(c)}
                className={`w-7 h-7 rounded-sm ${color===c ? 'ring-2 ring-white' : ''}`}
                style={{ background: c }}/>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/55">Timezone Filter</label>
          <input className="input w-full mt-1" value={timezoneFilter} onChange={e=>setTimezoneFilter(e.target.value)} placeholder="e.g. America/Denver"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/55">Call Script</label>
          <textarea className="input w-full mt-1 h-20" value={callScript} onChange={e=>setCallScript(e.target.value)} placeholder="Optional"/>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={autoDial} onChange={e=>setAutoDial(e.target.checked)} className="accent-civix-purple"/>
          Auto-dial leads when they enter this stage
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- */
function AddLeadsDrawer({ pipelineId, stage, onClose, onSubmit }) {
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(new Set());
  const { data } = useQuery({
    queryKey: ['leads-search', q],
    queryFn: () => apiGet(`/leads?limit=300${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  });
  const leads = data?.items || data?.leads || [];

  function toggle(id) {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  }
  function selectAll() { setPicked(new Set(leads.map(l=>l._id))); }

  return (
    <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} className="panel w-full md:max-w-2xl md:rounded-lg rounded-t-2xl md:max-h-[85vh] max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-civix-border/40 flex items-center justify-between">
          <div>
            <h3 className="font-display text-xl font-bold">Add Leads → <span className="gradient-text">{stage.name}</span></h3>
            <div className="text-sm text-white/55">Pick any leads to drop into this stage.</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-white/55"/></button>
        </div>
        <div className="p-5 border-b border-civix-border/40 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
            <input className="input pl-9 w-full" placeholder="Search name, phone, email, state…" value={q} onChange={e=>setQ(e.target.value)}/>
          </div>
          <button onClick={selectAll} className="btn-ghost text-sm whitespace-nowrap">Select all ({leads.length})</button>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {leads.length === 0 && <div className="text-center text-white/50 py-12">No matching leads.</div>}
          <div className="space-y-1.5">
            {leads.map(l => {
              const on = picked.has(l._id);
              return (
                <button key={l._id} onClick={()=>toggle(l._id)}
                  className={`w-full text-left p-2.5 rounded border transition flex items-center gap-3 ${
                    on ? 'border-civix-purple bg-civix-purple/15' : 'border-civix-border/30 bg-civix-bg/40 hover:border-civix-purple/40'
                  }`}>
                  <div className={`w-5 h-5 rounded-sm grid place-items-center border ${on ? 'bg-civix-purple border-civix-purple' : 'border-civix-border'}`}>
                    {on && <Check className="w-3 h-3 text-white"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{l.firstName} {l.lastName}</div>
                    <div className="text-xs text-white/50 truncate">
                      <span className="font-mono">{fmtPhone(l.phone)}</span> · {l.state || '—'} · {l.tier || 'Tier 2'}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-4 border-t border-civix-border/40 flex items-center justify-between">
          <div className="text-sm text-white/60">{picked.size} selected</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button disabled={picked.size===0} onClick={()=>onSubmit(Array.from(picked))}
              className="btn-primary disabled:opacity-50 flex items-center gap-2">
              <Plus className="w-4 h-4"/>Add {picked.size>0 ? picked.size : ''} to {stage.name}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
